import { findPreset } from '../model/colors.ts';
import { layoutNodes, polylineLength, toPxList, type LightNode, type Pt } from '../model/geometry.ts';
import type { LightStyle, Run, TimeOfDay } from '../model/types.ts';

/**
 * Pure, Skia-free description of everything the renderer needs, in image pixels.
 * Built once per change and shared by the live preview, exports and the AI guide.
 */
export interface SceneLight extends LightNode {
  color: string;
}

export interface SceneRun {
  id: string;
  /** Line color for the Lines view / legend. */
  lineColor: string;
  pts: Pt[];
  lengthPx: number;
  lights: SceneLight[];
}

export interface Scene {
  width: number;
  height: number;
  time: TimeOfDay;
  style: LightStyle;
  pxPerFt: number;
  /** Radius of the bright LED core, px (singles; also the fixture housing size). */
  coreRadius: number;
  /** Core radius actually drawn: for pairs, small enough that the two LEDs stay distinct. */
  emitterRadius: number;
  /** Distance between the two LEDs of a pair on the image, px (0 for singles). */
  pairGapPx: number;
  /** Length of the wall wash below each LED, px. */
  washLength: number;
  runs: SceneRun[];
}

export interface SceneInput {
  width: number;
  height: number;
  time: TimeOfDay;
  runs: Run[];
  style: LightStyle;
  colorId: string;
  customHex: string;
  /** Approximate real-world width covered by the photo, feet (sets the px-per-foot scale). */
  frameWidthFt: number;
}

/**
 * Apparent core radius of a 1–1.5 in faceted lens at night (glare makes it read larger than
 * the glass). The floor keeps LEDs visible when a whole house is in frame.
 */
const LENS_APPARENT_IN = 0.75;
const MIN_CORE_FRACTION = 0.0016;
/**
 * Minimum pair gap in core radii: below ~3 the two cores and halos merge into one blob. Only
 * kicks in at low resolution / small scale, where the true gap is a couple of pixels.
 */
const MIN_PAIR_GAP_CORES = 3.2;

export function buildScene(input: SceneInput): Scene {
  const { width, height, style } = input;
  const pxPerFt = width / Math.max(1, input.frameWidthFt);
  const pxPerIn = pxPerFt / 12;
  const rcBase = Math.max(LENS_APPARENT_IN * pxPerIn, MIN_CORE_FRACTION * width);
  const coreRadius = rcBase * style.size;
  const isPair = style.layout === 'pair';
  const pairGapPx = isPair ? Math.max(style.pairGapIn * pxPerIn, MIN_PAIR_GAP_CORES * rcBase) : 0;
  // Pairs: cap the core at a third of the gap so a dark dip always separates the two points.
  const emitterRadius = isPair ? Math.min(coreRadius, pairGapPx / 3) : coreRadius;

  // One light look for the whole house; line colors only group runs for pricing.
  const palette = findPreset(input.colorId, input.customHex).colors;
  // Pairs share a color by default; 'alternate' cycles the theme LED by LED (red-green, red-green…).
  const alternate = isPair && style.pairColors === 'alternate';
  const colorIndex = (n: LightNode) => (alternate ? n.group * 2 + n.member : n.group);
  const runs: SceneRun[] = input.runs.map((run) => {
    const pts = toPxList(run.points, width, height);
    const nodes = layoutNodes(pts, style.spacingIn * pxPerIn, style.layout, pairGapPx);
    return {
      id: run.id,
      lineColor: run.lineColor,
      pts,
      lengthPx: polylineLength(pts),
      lights: nodes.map((n) => ({ ...n, color: palette[colorIndex(n) % palette.length] })),
    };
  });

  return {
    width,
    height,
    time: input.time,
    style,
    pxPerFt,
    coreRadius,
    emitterRadius,
    pairGapPx,
    washLength: style.washFt * pxPerFt,
    runs,
  };
}

export function runLengthFt(run: SceneRun, pxPerFt: number): number {
  return run.lengthPx / pxPerFt;
}

export function lightCount(scene: Scene): number {
  return scene.runs.reduce((n, r) => n + r.lights.length, 0);
}
